import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, Send, Eye, Loader2, Mail, Users } from "lucide-react";
import api, { formatMoney, formatMoney2, formatPct, formatApiErrorDetail } from "../lib/api";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { ListaDReal } from "../components/ListaDReal";
import { SeguimientoListaD } from "../components/SeguimientoListaD";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const RANKS = [
  { k: "facturacion", label: "Fact." },
  { k: "ticket_medio_solo_libre", label: "TM libre" },
  { k: "ratio_consejo_para", label: "Consejo" },
  { k: "pct_parafarmacia", label: "% Para" },
];

async function getPdf(importId, vendedor) {
  const r = await api.get(`/sales/imports/${importId}/employee-report`, { params: { vendedor }, responseType: "blob" });
  return URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
}

function RankChip({ pos, n, label }) {
  const tone = pos === 1 ? "bg-emerald-600 text-white" : pos <= Math.ceil(n / 3) ? "bg-emerald-50 text-emerald-700"
    : pos > n - Math.ceil(n / 3) ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600";
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${tone}`}>{pos === 1 ? "TOP" : `#${pos}`} {label}</span>;
}

function Fila({ e, n, importId, onSent, i }) {
  const [busy, setBusy] = useState(null);
  const run = async (what) => {
    setBusy(what);
    try {
      if (what === "send") {
        const { data } = await api.post(`/sales/imports/${importId}/employee-report/send`, { vendedor: e.vendedor });
        toast.success(`Informe enviado a ${data.to}`);
        onSent();
      } else {
        const url = await getPdf(importId, e.vendedor);
        if (what === "view") window.open(url, "_blank");
        else Object.assign(document.createElement("a"), { href: url, download: `informe_${e.nombre}.pdf` }).click();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo completar la acción");
    } finally { setBusy(null); }
  };
  const btn = "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50";
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`informe-fila-${i}`}>
      <td className="py-3">
        <div className="font-semibold">{e.nombre}</div>
        <div className="text-[11px] text-slate-500">
          {e.email ? <span className="inline-flex items-center gap-1"><Mail size={10} />{e.email}</span>
            : <Link to="/empleados?tab=personal" className="text-amber-700 underline" data-testid={`informe-sin-email-${i}`}>Sin email · añadir</Link>}
        </div>
      </td>
      <td className="text-right font-mono text-sm">{formatMoney(e.facturacion)}</td>
      <td className="text-right font-mono text-sm hidden md:table-cell">{formatMoney2(e.ticket_medio_solo_libre)}</td>
      <td className="text-right font-mono text-sm hidden md:table-cell">{formatPct(e.ratio_consejo_para)}</td>
      <td className="hidden lg:table-cell">
        <div className="flex flex-wrap gap-1">{RANKS.map((r) => e.ranks[r.k] && <RankChip key={r.k} pos={e.ranks[r.k]} n={n} label={r.label} />)}</div>
      </td>
      <td className="text-xs text-slate-500 hidden sm:table-cell" data-testid={`informe-ultimo-envio-${i}`}>
        {e.ultimo_envio ? new Date(e.ultimo_envio).toLocaleDateString("es-ES") : "—"}
      </td>
      <td>
        <div className="flex justify-end gap-1.5">
          <button className={`${btn} border-slate-200 hover:border-slate-400`} onClick={() => run("view")} disabled={!!busy} data-testid={`informe-ver-${i}`}>
            {busy === "view" ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}<span className="hidden sm:inline"> Ver</span>
          </button>
          <button className={`${btn} border-slate-200 hover:border-slate-400`} onClick={() => run("download")} disabled={!!busy} data-testid={`informe-descargar-${i}`}>
            {busy === "download" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}<span className="hidden sm:inline"> PDF</span>
          </button>
          <button className={`${btn} border-slate-900 bg-slate-900 text-white hover:bg-slate-700`} onClick={() => run("send")} disabled={!!busy || !e.email || e.activo === false} data-testid={`informe-enviar-${i}`}>
            {busy === "send" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Enviar
          </button>
        </div>
      </td>
    </tr>
  );
}

function EnviarTodos({ data, importId, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const conEmail = data.empleados.filter((e) => e.email && e.activo !== false).length;
  const go = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/sales/imports/${importId}/employee-report/send-all`);
      toast.success(`Enviados ${r.enviados.length} informes` + (r.omitidos.length ? ` · ${r.omitidos.length} sin email o inactivos` : ""));
      if (r.errores.length) toast.error(`${r.errores.length} no se pudieron enviar: ${r.errores.map((x) => x.nombre).join(", ")}`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron enviar los informes");
    } finally { setBusy(false); setOpen(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} disabled={!conEmail || busy} data-testid="informes-enviar-todos-btn"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />} Enviar a todos ({conEmail})
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-white" data-testid="informes-enviar-todos-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar los informes de {data.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Cada uno de los {conEmail} empleados activos con email recibirá solo su propio informe, con un enlace a su PDF.
              {data.empleados.length - conEmail > 0 && ` ${data.empleados.length - conEmail} no lo recibirán por no tener email o estar inactivos.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="informes-enviar-todos-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={go} data-testid="informes-enviar-todos-confirmar">Enviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function Informes() {
  const [imports, setImports] = useState(null);
  const [importId, setImportId] = useState(null);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/sales/imports").then((r) => {
      const list = r.data.filter((i) => (i.employees || []).length > 0);
      setImports(list);
      if (list.length) setImportId(list[0].id);
    }).catch(() => setImports([]));
  }, []);
  useEffect(() => {
    if (!importId) return;
    setData(null);
    api.get("/informes/empleados", { params: { import_id: importId } }).then((r) => setData(r.data)).catch(() => setData({ empleados: [], n: 0 }));
  }, [importId, tick]);

  if (!imports) return <div className="p-8"><Loader2 className="animate-spin text-slate-400" /></div>;
  return (
    <div className="p-4 sm:p-8 max-w-7xl fade-in" data-testid="informes-page">
      <BannerFarmanaliza compact image="/img/farmanaliza-informes.jpg?v=2" kicker="Informes" subtitle="Informes de cada empleado"
                         text="Genera, revisa y envía por email el informe PDF de cada persona del equipo." testid="banner-informes" />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1.5"><FileText size={12} /> Equipo</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Informes por empleado</h1>
          <p className="text-slate-500 mt-2 text-sm max-w-2xl">
            Informe por empleado: sus indicadores y su puesto frente al equipo, el consejo (qué aconseja mejor y qué debe reforzar),
            el stock D que ha vendido, su evolución y la lista de stock D real a recomendar. Revísalo y envíalo solo cuando lo creas conveniente.
          </p>
        </div>
        {imports.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <select className="input text-sm py-1.5" style={{ width: 190 }} value={importId || ""} onChange={(e) => setImportId(e.target.value)} data-testid="informes-mes-select">
              {imports.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
            </select>
            {data && <EnviarTodos data={data} importId={importId} onDone={() => setTick((t) => t + 1)} />}
          </div>
        )}
      </div>

      {!imports.length ? (
        <div className="card p-8 max-w-lg" data-testid="informes-sin-datos">
          <p className="text-slate-600 mb-4">Sube primero un mes de ventas con empleados.</p>
          <Link to="/subidas/ventas" className="btn btn-primary">Subir ventas</Link>
        </div>
      ) : !data ? (
        <div className="flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando equipo…</div>
      ) : (
        <>
        <SeguimientoListaD importId={importId} />
        <ListaDReal importId={importId} />
        <div className="card p-4 sm:p-6 overflow-x-auto" data-testid="informes-tabla">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2">Empleado</th>
                <th className="text-right">Facturación</th>
                <th className="text-right hidden md:table-cell">TM venta libre</th>
                <th className="text-right hidden md:table-cell">Consejo</th>
                <th className="text-left hidden lg:table-cell pl-2">Puesto en el equipo (de {data.n})</th>
                <th className="text-left hidden sm:table-cell">Último envío</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.empleados.map((e, i) => <Fila key={e.vendedor} e={e} i={i} n={data.n} importId={importId} onSent={() => setTick((t) => t + 1)} />)}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
